import { Component, OnInit, Input, ViewChild, ChangeDetectorRef, DoCheck } from '@angular/core';
import { Subscription, BehaviorSubject } from 'rxjs';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { getCurrencySymbol } from '@angular/common';
import { AdvanceIndicatorFormComponent } from '../advance-indicator-form/advance-indicator-form.component';
import { ExitFormReturnI, AlgoStrategy, NgxToastrService } from '@shared/providers';
import { BasicIndicatorFormComponent } from '../basic-indicator-form/basic-indicator-form.component';
import { PRICE_METHOD, FINAL_EXECUTION_METHOD, StrategyFormBuildMode } from '../../strategy.enums';
import { Utils, MIN_PERCENTAGE_VALUE, MIN_ABSOLUTE_VALUE, MAX_PERCENTAGE, MAX_PERCENTAGE_100,
         MAX_ABSOLUTE_VALUE, DEFAULT_PRECISION, DEFAULT_TRAILING_TICKS } from '@app/_helpers';
import { map } from 'rxjs/operators';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';

@Component({
  selector: 'app-strategy-take-exit',
  templateUrl: './strategy-take-exit.component.html',
  styleUrls: ['./strategy-take-exit.component.scss']
})
export class StrategyTakeExitComponent implements OnInit, DoCheck {
  StrategyFormBuildMode = StrategyFormBuildMode;
  @Input() strategyMode$: BehaviorSubject<StrategyFormBuildMode>;
  @ViewChild(AdvanceIndicatorFormComponent) advanceIndicatorFormRef: AdvanceIndicatorFormComponent;
  @ViewChild(BasicIndicatorFormComponent) basicIndicatorFormRef: BasicIndicatorFormComponent;
  fillFormIntervalRef;

  update: AlgoStrategy;
  useConditionalExit = false;
  private subscriptions = new Subscription();
  currencySymbol = getCurrencySymbol('INR', 'narrow');
  PRICE_METHOD = PRICE_METHOD;
  FINAL_EXECUTION_METHOD = FINAL_EXECUTION_METHOD;
  MIN_PERCENTAGE = MIN_PERCENTAGE_VALUE;
  MIN_ABSOLUTE_VALUE = MIN_ABSOLUTE_VALUE;
  MAX_ABSOLUTE_VALUE = MAX_ABSOLUTE_VALUE;
  MAX_PERCENTAGE = MAX_PERCENTAGE;
  MAX_PERCENTAGE_100 = MAX_PERCENTAGE_100;

  DEFAULT_PRECISION = DEFAULT_PRECISION;

  exitForm = new UntypedFormGroup({
    takeProfit: new UntypedFormControl(null, [Validators.required, Validators.min(0.01), Validators.max(MAX_ABSOLUTE_VALUE)]),
    stopLoss: new UntypedFormControl(null, [Validators.required, Validators.min(0.01), Validators.max(MAX_PERCENTAGE_100)]),
    enableTrailingTicks: new UntypedFormControl(null, []),
    trailingTicks: new UntypedFormControl(null, [Validators.min(1)]),
    isPercent: new UntypedFormControl(null, []),
    // userPercentValueProfit: new FormControl(false, [Validators.required]),
    // bestBidAsk: new FormControl(null, []),
    // modTick: new FormControl(null, []),
    // modCount: new FormControl(null, []),
    // timer: new FormControl(null, []),
    // marketOrder: new FormControl(null, []),
    // userPercentValueStopLoss: new FormControl(false, [Validators.required]),
    // limitOrder: new FormControl(null, []),
    // limitPrice: new FormControl(null, []),
    // trailingStopLoss: new FormControl(false, [Validators.required]),
    // trailingTicks: new FormControl(null, [])
  });
  minValue: number;
  maxValue: number;

  constructor(private snackbar: NgxToastrService, private changeDetectorRef: ChangeDetectorRef) { }

  onValueTypeChange() {
    let currentProfitValue = this.exitForm.get('takeProfit').value;
    let currentStoplossValue = this.exitForm.get('stopLoss').value;
    const isPercent = this.exitForm.get('isPercent').value;
    this.minValue = isPercent ? MIN_PERCENTAGE_VALUE : MIN_ABSOLUTE_VALUE;
    this.maxValue = isPercent ? MAX_PERCENTAGE : MAX_ABSOLUTE_VALUE;

    const maxStopLoss = isPercent ? MAX_PERCENTAGE_100 : MAX_ABSOLUTE_VALUE;
    const minStopLoss = isPercent ? MIN_PERCENTAGE_VALUE : MIN_ABSOLUTE_VALUE;

    if (currentProfitValue < this.minValue) {
      currentProfitValue = this.minValue;
    }
    if (currentStoplossValue < minStopLoss) {
      currentStoplossValue = minStopLoss;
    }
    if (currentProfitValue > this.maxValue) {
      currentProfitValue = this.maxValue;
    }
    if (currentStoplossValue > maxStopLoss) {
      currentStoplossValue = maxStopLoss;
    }

    this.exitForm.get('takeProfit').setValue(currentProfitValue.toString());
    this.exitForm.get('stopLoss').setValue(currentStoplossValue.toString());
    this.exitForm.get('takeProfit').setValidators([Validators.required, Validators.min(this.minValue), Validators.max(this.maxValue)]);
    this.exitForm.get('stopLoss').setValidators([Validators.required, Validators.min(minStopLoss), Validators.max(maxStopLoss)]);
    this.exitForm.get('takeProfit').updateValueAndValidity();
    this.exitForm.get('stopLoss').updateValueAndValidity();
  }

  ngOnInit(): void {
    this.strategyMode$.subscribe(mode => {
      if (!this.update) { return; }
      const isAdvance = (mode === StrategyFormBuildMode.WIZARD);

      if (mode === StrategyFormBuildMode.REGULAR) {
        // Regular mode does not support absolute values
        this.exitForm.get('isPercent').setValue(true);
        // Updating the percentage field and updating the validations
        const isPercentage = this.isPercentage;
        this.exitForm.get('takeProfit').markAsUntouched();
        this.exitForm.get('stopLoss').markAsUntouched();
      }

      /**
       * ==========  Ticket ======
       * Done for RA-429. 
       * 
       * ========== Scenario ======
       * To prevent a case in which default condition is filled twice when 
       * switched to wizard mode. That issue occurred when we switch to wizard mode as soon
       * as form is loaded. When form is opened the timeout for filling the condition form starts
       * When if user switches to wizard mode before that timeout is completed the below timeout 
       * to fill form is triggered (as we have to fill advance form now instead of previously 
       * filled basic form). So 2 timeouts for filling same form are running therefore form 
       * is filled twice
       * 
       * 
       * ========= Fix ==========
       * 
       * `fillFormIntervalRef` contains reference of the timeouts triggered for filling form
       * Before filling form we check weather any other timeout was triggered to fill the form
       * If Yes, cancel that timeout and fill values here.
       */


      if (this.fillFormIntervalRef) { clearInterval(this.fillFormIntervalRef); }
      this.fillFormIntervalRef = setTimeout(() => {
        if (isAdvance) {
          this.advanceIndicatorFormRef?.fillUpdateForm(this.update.exitIndicatorRow);
        } else {
          this.basicIndicatorFormRef?.fillUpdateForm(this.update.exitIndicatorRow);
        }
      }, 1000)
    })
  }

  ngDoCheck(): void {
    this.changeDetectorRef.detectChanges()
  }

  getData(): ExitFormReturnI {
    this.exitForm.markAllAsTouched();
    if (!this.isValid) {
      Utils.getFormValidationErrors(this.exitForm);
      return;
    }

    const data = this.exitForm.value as ExitFormReturnI;
    const indicatorData = (this.basicIndicatorFormRef || this.advanceIndicatorFormRef)?.getData();
    data.indicatorRow = indicatorData?.indicatorRow;
    data.rowComparator = indicatorData?.rowComparator;
    return data;
  }

  fillUpdateForm(update: AlgoStrategy) {
    this.exitForm.patchValue(update);
    this.update = update;

    if (this.update.trailingTicks) {
      this.exitForm.get('trailingTicks').setValidators([Validators.required]);
      this.exitForm.get('trailingTicks').enable();
      this.exitForm.get('enableTrailingTicks').setValue(true);
    }
    else {
      this.exitForm.get('trailingTicks').setValidators([]);
      this.exitForm.get('trailingTicks').disable();
      this.exitForm.get('trailingTicks').setValue(DEFAULT_TRAILING_TICKS);
    }
    this.exitForm.get('trailingTicks').updateValueAndValidity();

    if (update.exitIndicatorRow.indicatorRow) {
      if(this.strategyMode$.value != StrategyFormBuildMode.REGULAR) {
        this.useConditionalExit = true;
      }
      setTimeout(() => {
        try {
          (this.basicIndicatorFormRef || this.advanceIndicatorFormRef).fillUpdateForm(update.exitIndicatorRow);
        } catch (error) { }
      }, 1000);
    } else {
      this.useConditionalExit = false;
    }
    this.exitForm.get('takeProfit').setValue(parseFloat(this.update.takeProfit).toFixed(DEFAULT_PRECISION));
    this.exitForm.get('stopLoss').setValue(parseFloat(this.update.stopLoss).toFixed(DEFAULT_PRECISION));
  }

  setDefaultFields(update: AlgoStrategy, val: StrategyFormBuildMode) {
    this.exitForm.get('trailingTicks').setValidators([]);
    this.exitForm.get('trailingTicks').disable();
    this.exitForm.get('enableTrailingTicks').setValue(false);
    this.exitForm.get('trailingTicks').setValue(DEFAULT_TRAILING_TICKS);

    this.exitForm.get('trailingTicks').updateValueAndValidity();
  }

  get isValid() {
    return this.exitForm.valid && 
    (this.advanceIndicatorFormRef ? this.advanceIndicatorFormRef.isValid : true) &&
    (this.basicIndicatorFormRef ? this.basicIndicatorFormRef.isValid : true);
  }

  changeStopLossValidity(event: MatCheckboxChange) {
    if (event.checked) {
      this.exitForm.get('trailingTicks').setValidators([Validators.required, Validators.min(1)]);
      this.exitForm.get('trailingTicks').setValue(1);
      this.exitForm.get('trailingTicks').enable();
    } else {
      this.exitForm.get('trailingTicks').setValidators([]);
      this.exitForm.get('trailingTicks').disable();
    }
    this.exitForm.get('trailingTicks').updateValueAndValidity();
  }

  get isWizardMode() {
    return this.strategyMode$.pipe(
      map(val => val === StrategyFormBuildMode.WIZARD)
    )
  }

  get isPercentage() {
    const isPercent = this.exitForm.get('isPercent').value;
    this.minValue = isPercent ? MIN_PERCENTAGE_VALUE : MIN_ABSOLUTE_VALUE;
    this.maxValue = isPercent ? MAX_PERCENTAGE : MAX_ABSOLUTE_VALUE;

    const maxStopLoss = isPercent ? MAX_PERCENTAGE_100 : MAX_ABSOLUTE_VALUE;
    const minStopLoss = isPercent ? MIN_PERCENTAGE_VALUE : MIN_ABSOLUTE_VALUE;

    this.exitForm.get('takeProfit').setValidators([Validators.required, Validators.min(this.minValue), Validators.max(this.maxValue)]);
    this.exitForm.get('stopLoss').setValidators([Validators.required, Validators.min(minStopLoss), Validators.max(maxStopLoss)]);
    this.exitForm.get('takeProfit').updateValueAndValidity();
    this.exitForm.get('stopLoss').updateValueAndValidity();
    return isPercent;
  }
}